import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Tooltip,
} from '@mui/material';
import { DataGrid, zhCN } from '@mui/x-data-grid';

import { useColumnVisibility } from './ColumnVisibility';

const defaultTextColumnMinWidth = 260;
const defaultRowsPerPageOptions = [100];
const emptyColumns = [];
const emptyRows = [];
const dataGridLocaleText = zhCN.components.MuiDataGrid.defaultProps.localeText;
const numericFieldPattern = /(^|_)(imps|clicks|ctr|stay|rate|ratio|percent|jump\d*|landing|cost|price|amount|total|count|cnt|uv|pv)($|_|\d)/i;
const narrowFieldMinWidths = {
  id: 72,
  job: 76,
  parent: 76,
};
const truncateTextSx = {
  display: 'block',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
// MUI X v6 applies the compact density factor (0.7) to this base height,
// producing the existing stable 36px report rows without lazy measurement.
const reportRowHeight = 52;
const getTogglableColumns = (gridColumns) => (
  gridColumns
    .filter(column => !String(column.field).startsWith('__'))
    .map(column => column.field)
);

function getColumnMinWidth(columns, col) {
  return columns[col]?.minWidth || columns[col]?.width || narrowFieldMinWidths[col] || 100;
}

function isNumericColumn(columns, col) {
  const column = columns[col] || {};
  return column.numeric || numericFieldPattern.test(col);
}

function getColumnAlign(columns, col) {
  const column = columns[col] || {};
  if (column.align) return column.align;
  if (column.truncate) return 'left';
  if (isNumericColumn(columns, col)) return 'right';
  return 'center';
}

function getColumnSizing(columns, col) {
  const column = columns[col] || {};
  if (column.truncate) {
    const minWidth = column.minWidth || column.width || defaultTextColumnMinWidth;
    return {
      minWidth,
      flex: column.flex || 3,
    };
  }

  return {
    minWidth: getColumnMinWidth(columns, col),
    flex: column.flex || 1,
  };
}

const ReportTable = ({
  data,
  columns,
  getColumnLabel,
  renderCell,
  getCellTitle,
  getRowKey,
  page,
  rowsPerPage,
  onPagination,
  rowsPerPageOptions = defaultRowsPerPageOptions,
  apiRef,
  showColumnSettings,
  columnSettingsTableId,
  columnSettingsUserId,
  columnSettingsAnchorEl,
  loading,
  sx,
}) => {
  const dataColumns = data?.columns || emptyColumns;
  const rows = data?.rows || emptyRows;
  const rowCount = Number.isFinite(Number(data?.total)) ? Number(data?.total) : rows.length;
  const gridRows = useMemo(() => rows.map((row, index) => ({
    ...row,
    __reportTableRowId: `${getRowKey(row)}:${index}`,
  })), [getRowKey, rows]);

  const renderCellContent = useCallback((col, row) => {
    const content = renderCell ? renderCell(col, row) : row[col];
    if (!columns[col]?.truncate) return content;

    const title = getCellTitle ? getCellTitle(col, row) : row[col];
    return (
      <Tooltip title={title == null ? '' : String(title)}>
        <Box component="span" sx={truncateTextSx}>
          {content}
        </Box>
      </Tooltip>
    );
  }, [columns, getCellTitle, renderCell]);

  const gridColumns = useMemo(() => dataColumns.map((col) => {
    const align = getColumnAlign(columns, col);
    return {
      field: col,
      headerName: getColumnLabel ? getColumnLabel(col) : columns[col]?.label || col,
      ...getColumnSizing(columns, col),
      align,
      headerAlign: align,
      sortable: false,
      filterable: false,
      hideable: columns[col]?.hideable !== false,
      renderCell: (params) => renderCellContent(col, params.row),
    };
  }), [columns, dataColumns, getColumnLabel, renderCellContent]);
  const visibilityColumns = useMemo(() => dataColumns.map((col) => ({
    field: col,
    hideable: columns[col]?.hideable !== false,
    defaultHidden: columns[col]?.defaultHidden === true,
  })), [columns, dataColumns]);
  const {
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  } = useColumnVisibility({
    columns: visibilityColumns,
    tableId: showColumnSettings ? columnSettingsTableId : null,
    userId: columnSettingsUserId,
  });
  const gridSlotProps = useMemo(() => (
    showColumnSettings ? {
      ...(columnSettingsAnchorEl ? {
        panel: {
          anchorEl: columnSettingsAnchorEl,
          placement: 'bottom-end',
        },
      } : {}),
      columnsPanel: {
        getTogglableColumns,
      },
    } : undefined
  ), [columnSettingsAnchorEl, showColumnSettings]);

  const paginationModel = useMemo(() => ({
    page,
    pageSize: rowsPerPage,
  }), [page, rowsPerPage]);

  const handlePaginationModelChange = useCallback((model) => {
    if (model.pageSize !== rowsPerPage) {
      onPagination('limit', model.pageSize);
      return;
    }

    if (model.page !== page) {
      onPagination('page', model.page);
    }
  }, [onPagination, page, rowsPerPage]);

  const paperSx = useMemo(() => ([
    {p: 1, pb: 0},
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ]), [sx]);

  return (
    <Paper sx={paperSx}>
      <DataGrid
        autoHeight
        apiRef={apiRef}
        columnVisibilityModel={showColumnSettings ? columnVisibilityModel : undefined}
        columns={gridColumns}
        density="compact"
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={(row) => row.__reportTableRowId}
        loading={loading ?? !data}
        localeText={dataGridLocaleText}
        onColumnVisibilityModelChange={showColumnSettings ? onColumnVisibilityModelChange : undefined}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={rowsPerPageOptions}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        rowHeight={reportRowHeight}
        rowCount={rowCount}
        rows={gridRows}
        scrollbarSize={0}
        slotProps={gridSlotProps}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'clip',
            overflowWrap: 'anywhere',
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            minWidth: 0,
          },
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            boxSizing: 'border-box',
            whiteSpace: 'normal',
            lineHeight: 1.25,
            minHeight: 36,
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            py: 0.5,
          },
          '& .MuiDataGrid-cellContent': {
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'clip',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          },
          '& .MuiDataGrid-columnHeader': {
            boxSizing: 'border-box',
          },
        }}
      />
    </Paper>
  );
};

ReportTable.propTypes = {
  data: PropTypes.object,
  columns: PropTypes.object.isRequired,
  getColumnLabel: PropTypes.func,
  renderCell: PropTypes.func,
  getCellTitle: PropTypes.func,
  getRowKey: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPagination: PropTypes.func.isRequired,
  rowsPerPageOptions: PropTypes.array,
  apiRef: PropTypes.object,
  showColumnSettings: PropTypes.bool,
  columnSettingsTableId: PropTypes.string,
  columnSettingsUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  columnSettingsAnchorEl: PropTypes.object,
  loading: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.func, PropTypes.object]),
};

ReportTable.defaultProps = {
  showColumnSettings: false,
};

export default ReportTable;
